import type { AxiosError } from "@shared/services/apiClient";
import type { RapidEventType } from "../utils/rapidEventValidation";
import type { AnnotationCategoryFilter } from "../components/eegViewerChromeShared";

/**
 * Shared annotation-panel + consensus-review types/helpers used by
 * `useEEGAnnotationPanel` and `useEEGConsensusReview` (and mirrored, where
 * useful, by EEGViewer's own long-standing local copies). Kept separate from
 * EEGViewer.tsx so the extracted hooks never need to import value symbols
 * from the viewer component itself (which would create a circular import).
 */

export interface Annotation {
  annotation_id?: string;
  study_id?: string;
  user_id?: string;
  recording_id?: string | null;
  annotation_type: string;
  annotation_type_id?: string | null;
  label: string;
  description?: string | null;
  notes?: string | null;
  start_time: number;
  end_time?: number;
  duration?: number | null;
  channel_scope?: "all" | "channel_set";
  channels_affected: string[];
  color?: string;
  confidence_level?: number | null;
  is_reviewed?: boolean;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  user?: {
    user_id?: string;
    user_name?: string;
    first_name?: string;
    last_name?: string;
  } | null;
  reviewer?: {
    user_id?: string;
    user_name?: string;
    first_name?: string;
    last_name?: string;
  } | null;
}

export interface InterventionResponse {
  response_id: string;
  observed_at_seconds: number;
  response_window_seconds?: number | null;
  observed_effect?: string | null;
  severity_delta?: number | null;
  notes?: string | null;
}

export interface InterventionEvent {
  intervention_id: string;
  study_id: string;
  recording_id?: string | null;
  event_time_seconds: number;
  event_type: string;
  medication_name?: string | null;
  medication_dose?: string | null;
  medication_route?: string | null;
  procedure_name?: string | null;
  reason?: string | null;
  expected_effect?: string | null;
  source?: string | null;
  linked_annotation_id?: string | null;
  linked_annotation?: {
    annotation_id: string;
    annotation_type: string;
    label: string;
    start_time: number;
    description?: string | null;
  } | null;
  responses?: InterventionResponse[];
}

export interface AnnotationNoteComposerState {
  title: string;
  placeholder: string;
  defaultValue: string;
  requireText?: boolean;
  onSubmit: (note: string) => Promise<void> | void;
  onCancel?: () => void;
}

export type AnnotationPanelTab =
  | "annotate"
  | "review"
  | "channels"
  | "trend_event";

export const ANNOTATION_PANEL_TABS: AnnotationPanelTab[] = [
  "annotate",
  "review",
  "channels",
  "trend_event",
];

export type SmartSuggestionSource = "trend_marker";

export type TrendMarkerType =
  | "threshold_crossing"
  | "abrupt_power_shift"
  | "asymmetry_spike";

export type SmartAnnotationSuggestion = {
  id: string;
  source: SmartSuggestionSource;
  annotationType: string;
  label: string;
  color: string;
  startTime: number;
  defaultDurationSeconds: number;
  confidence: number;
  explanation: string;
  channels: string[];
  markerType: TrendMarkerType;
  metric: "delta_power" | "total_power" | "alpha_power" | "asymmetry_pct";
};

export type SmartAnnotationSuggestionDraft = {
  label: string;
  startTime: string;
  durationSeconds: string;
  notes: string;
};

export interface ConsensusReviewerAnnotation {
  reviewerId: string;
  reviewerName: string;
  reviewerRole: string;
  annotationType: string;
  label: string;
  confidence: number;
}

export interface ConsensusDisagreement {
  annotationId: string;
  startTime: number;
  endTime?: number;
  reviewers: ConsensusReviewerAnnotation[];
  resolutionMethod?: string;
  resolvedBy?: string;
  resolvedAt?: string;
  finalDecision?: string;
}

export interface ConsensusAutoSummary {
  studyId: string;
  kappaType: "cohen" | "fleiss" | string;
  reviewerCount: number;
  kappa: number;
  interpretation: string;
  agreementPercentage: number;
  totalAnnotations: number;
  agreedAnnotations: number;
  disagreedAnnotations: number;
}

export interface ConsensusReportSummary {
  studyId: string;
  totalAnnotations: number;
  consensusReached: number;
  consensusPercentage: number;
  disagreements: ConsensusDisagreement[];
  kappaScore: number;
  recommendations: string[];
}

export interface ConsensusDisagreementSummary {
  studyId: string;
  totalDisagreements: number;
  disagreements: ConsensusDisagreement[];
  unresolvedCount: number;
}

export interface ConsensusReviewerSearchResult {
  user_id: string;
  user_name?: string;
  full_name?: string;
  email?: string;
  role?: string;
}

export interface ConsensusReviewerAssignmentSummary {
  assignment_id: string;
  study_id: string;
  user_id: string;
  user_name?: string;
  full_name?: string;
  email?: string;
  role?: string;
  is_blind: boolean;
  is_completed: boolean;
  assigned_at: string;
  completed_at?: string | null;
}

export interface ConsensusReviewerAssignmentResponse {
  studyId: string;
  reviewerCount: number;
  blindReview: boolean;
  completedCount: number;
  assignments: ConsensusReviewerAssignmentSummary[];
}

export const EMPTY_CONSENSUS_DISAGREEMENTS: ConsensusDisagreement[] = [];

export function toUserFacingApiError(error: unknown): {
  status: number | null;
  message: string;
} {
  const axiosError = error as AxiosError<{ error?: string; message?: string }>;
  const status = axiosError?.response?.status ?? null;
  const message =
    axiosError?.response?.data?.error ||
    axiosError?.response?.data?.message ||
    axiosError?.message ||
    "Request failed.";
  return { status, message };
}

export const RAPID_EVENT_LABELS: Record<RapidEventType, string> = {
  seizure_onset: "Seizure Onset",
  seizure_offset: "Seizure Offset",
  medication_administered: "Medication",
  procedure_marker: "Procedure",
  clinical_note: "Clinical Note",
};

export const RAPID_EVENT_COLORS: Record<RapidEventType, string> = {
  seizure_onset: "#ef4444",
  seizure_offset: "#22c55e",
  medication_administered: "#3b82f6",
  procedure_marker: "#8b5cf6",
  clinical_note: "#f59e0b",
};

export function toRapidEventType(value: string): RapidEventType | null {
  if (
    value === "seizure_onset" ||
    value === "seizure_offset" ||
    value === "medication_administered" ||
    value === "procedure_marker" ||
    value === "clinical_note"
  ) {
    return value;
  }
  return null;
}

export function buildInterventionLabel(event: InterventionEvent): string {
  const rapidEventType = toRapidEventType(event.event_type);
  const baseLabel = rapidEventType
    ? RAPID_EVENT_LABELS[rapidEventType]
    : event.event_type;
  if (event.event_type === "medication_administered" && event.medication_name) {
    return `${baseLabel}: ${event.medication_name}`;
  }
  if (event.event_type === "procedure_marker" && event.procedure_name) {
    return `${baseLabel}: ${event.procedure_name}`;
  }
  if (event.reason) {
    return `${baseLabel}: ${event.reason}`;
  }
  return baseLabel;
}

export const DEFAULT_ANNOTATION_CATEGORY_FILTER: AnnotationCategoryFilter =
  "all";

export function isAnnotationCategoryFilter(
  value: unknown,
): value is AnnotationCategoryFilter {
  return (
    value === "all" || value === "clinical" || value === "technical_imported"
  );
}

export function readJsonFromSessionStorage<T>(
  storageKey: string,
  fallback: T,
): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as T;
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

export function normalizeAnnotationTypeCode(value?: string | null): string {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

export function isTechnicalImportedAnnotationTypeCode(
  value?: string | null,
): boolean {
  const normalized = normalizeAnnotationTypeCode(value);
  return (
    normalized === "edf_import" ||
    normalized.startsWith("edf_") ||
    normalized.startsWith("technical_") ||
    normalized.includes("import")
  );
}

export type AnnotationCategoryBucket = Exclude<AnnotationCategoryFilter, "all">;
